import { motion } from "framer-motion";

const cardMotion = (delay, floatDuration) => ({
  initial: { opacity: 0, scale: 0.9, y: 24 },
  animate: { opacity: 1, scale: 1, y: [0, -10, 0] },
  transition: {
    opacity: { duration: 0.6, delay },
    scale: { duration: 0.6, delay },
    y: {
      duration: floatDuration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay + 0.6,
    },
  },
});

/* ---------- Kanban board: task chip slides across columns, clipped safely ---------- */
const KanbanCard = () => (
  <motion.div
    {...cardMotion(0.2, 5.5)}
    className="absolute top-[2%] left-[4%] z-10 w-[180px] rounded-xl bg-white border border-slate-200 shadow-lg p-3 -rotate-3"
  >
    <p className="text-[10px] font-semibold text-[#003333] mb-2">
      Project board
    </p>
    <div className="relative grid grid-cols-3 gap-2 h-16 overflow-hidden">
      {[0, 1, 2].map((col) => (
        <div key={col} className="rounded-md bg-slate-50 h-full" />
      ))}
      <motion.div
        className="absolute top-1 left-1 w-10 h-5 rounded bg-[#66B3B3] shadow-sm"
        animate={{ x: [0, 0, 58, 58, 116, 116, 0], opacity: 1 }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.15, 0.3, 0.45, 0.6, 0.85, 1],
        }}
      />
      <motion.div
        className="absolute top-8 left-1 w-10 h-5 rounded bg-[#003333]/70 shadow-sm"
        animate={{ x: [0, 58, 58, 58] }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.6,
          times: [0, 0.4, 0.7, 1],
        }}
      />
    </div>
  </motion.div>
);

/* ---------- Website: browser mockup that reflows desktop -> mobile and clicks a CTA ---------- */
const WebsiteCard = () => (
  <motion.div
    {...cardMotion(0.35, 6.2)}
    className="absolute top-[0%] right-[2%] z-20 w-[200px] rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden rotate-2"
  >
    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50">
      <span className="w-2 h-2 rounded-full bg-slate-300" />
      <span className="w-2 h-2 rounded-full bg-slate-300" />
      <span className="w-2 h-2 rounded-full bg-slate-300" />
      <span className="ml-1 text-[9px] text-slate-400">lolcorp.co.uk</span>
      <div className="ml-auto flex items-center gap-1">
        <motion.svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          animate={{ opacity: [1, 1, 0.35, 0.35, 1] }}
          transition={{ duration: 6.2, repeat: Infinity, times: [0, 0.42, 0.5, 0.9, 1] }}
        >
          <rect x="2" y="4" width="20" height="13" rx="1.5" stroke="#94a3b8" strokeWidth="2" />
          <path d="M8 20h8" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        </motion.svg>
        <motion.svg
          width="7"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          animate={{ opacity: [0.35, 0.35, 1, 1, 0.35] }}
          transition={{ duration: 6.2, repeat: Infinity, times: [0, 0.42, 0.5, 0.9, 1] }}
        >
          <rect x="6" y="2" width="12" height="20" rx="2" stroke="#94a3b8" strokeWidth="2" />
        </motion.svg>
      </div>
    </div>

    <div className="p-3">
      {/* nav row */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="w-3 h-3 rounded-sm bg-[#003333]" />
        <div className="flex gap-1.5">
          <span className="h-1 w-4 rounded-full bg-slate-200" />
          <span className="h-1 w-4 rounded-full bg-slate-200" />
          <span className="h-1 w-4 rounded-full bg-slate-200" />
        </div>
      </div>

      {/* hero block that reflows from a 2-col desktop layout to stacked mobile */}
      <motion.div
        className="mx-auto"
        animate={{ width: ["100%", "58%", "100%"] }}
        transition={{ duration: 6.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="h-2.5 w-4/5 rounded-full bg-[#003333]/70 mb-1.5" />
        <div className="h-1.5 w-full rounded-full bg-slate-200 mb-1" />
        <div className="h-1.5 w-2/3 rounded-full bg-slate-200 mb-2" />
        <div className="h-9 w-full rounded-md bg-[#66B3B3]/15 border border-[#66B3B3]/30 mb-2" />

        <div className="relative flex justify-end">
          <motion.div
            className="text-[9px] font-medium text-white bg-[#66B3B3] rounded-full px-2.5 py-1"
            animate={{ scale: [1, 1, 0.9, 1, 1] }}
            transition={{ duration: 6.2, repeat: Infinity, ease: "easeInOut", times: [0, 0.44, 0.5, 0.56, 1] }}
          >
            Get started
          </motion.div>
          <motion.div
            className="absolute w-2 h-2 rounded-full bg-[#003333] shadow"
            animate={{
              top: [16, 16, 2, 2, 16],
              right: [46, 20, 6, 6, 46],
              opacity: [0, 1, 1, 0, 0],
            }}
            transition={{
              duration: 6.2,
              repeat: Infinity,
              ease: "easeInOut",
              times: [0, 0.32, 0.46, 0.58, 1],
            }}
          />
        </div>
      </motion.div>
    </div>
  </motion.div>
);

/* ---------- Client hub: the centrepiece - growing client list, not niche records ---------- */
const ClientHubCard = () => {
  const clients = [
    { initials: "JM", name: "Jonas M.", status: "Active", color: "#66B3B3" },
    { initials: "PL", name: "Priya L.", status: "Active", color: "#003333" },
    { initials: "RS", name: "Rowan S.", status: "Onboarding", color: "#94a3b8" },
  ];
  return (
    <motion.div
      {...cardMotion(0.55, 7)}
      className="absolute top-[26%] left-[22%] z-30 w-[240px] rounded-2xl bg-white border border-slate-200 shadow-2xl p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold text-[#003333]">Client hub</p>
        <div className="relative">
          <span className="text-[10px] text-slate-400">128 clients</span>
          <motion.span
            className="absolute -top-4 right-0 text-[9px] font-medium text-[#66B3B3] bg-[#66B3B3]/10 rounded-full px-1.5 py-0.5 whitespace-nowrap"
            animate={{ opacity: [0, 0, 1, 1, 0], y: [4, 4, 0, 0, -4] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              times: [0, 0.55, 0.65, 0.85, 1],
              ease: "easeInOut",
            }}
          >
            +1 new
          </motion.span>
        </div>
      </div>
      <div className="space-y-2">
        {clients.map((c, i) => (
          <motion.div
            key={c.name}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5"
            animate={{ backgroundColor: ["#ffffff", "#f8fafc", "#ffffff"] }}
            transition={{ duration: 5, repeat: Infinity, delay: i * 1.5, ease: "easeInOut" }}
          >
            <span
              className="flex items-center justify-center w-6 h-6 rounded-full text-white text-[9px] font-semibold shrink-0"
              style={{ backgroundColor: c.color }}
            >
              {c.initials}
            </span>
            <span className="text-[11px] text-slate-700 flex-1">{c.name}</span>
            <span
              className="text-[9px] font-medium px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: c.status === "Active" ? "rgba(102,179,179,0.15)" : "#f1f5f9",
                color: c.status === "Active" ? "#004D4D" : "#64748b",
              }}
            >
              {c.status}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

/* ---------- Throughput: bars each with their own rhythm ---------- */
const ThroughputCard = () => {
  const bars = [
    { base: 40, dur: 3.2 },
    { base: 70, dur: 4.1 },
    { base: 55, dur: 3.6 },
    { base: 90, dur: 2.8 },
    { base: 65, dur: 3.9 },
  ];
  return (
    <motion.div
      {...cardMotion(0.75, 6)}
      className="absolute top-[52%] right-[0%] z-20 w-[160px] rounded-xl bg-white border border-slate-200 shadow-lg p-3 rotate-2"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold text-[#003333]">Throughput</p>
        <motion.span
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="w-1.5 h-1.5 rounded-full bg-[#66B3B3]"
        />
      </div>
      <div className="flex items-end gap-1 h-12">
        {bars.map((b, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-sm bg-[#003333]/80"
            animate={{ height: [`${b.base * 0.5}%`, `${b.base}%`, `${b.base * 0.6}%`] }}
            transition={{ duration: b.dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
          />
        ))}
      </div>
    </motion.div>
  );
};

/* ---------- Team chat: messages type in and pop up, looping ---------- */
const ChatCard = () => {
  const cycle = 7.5;
  const msgs = [
    { start: 0.1, text: "Ticket #402 assigned", mine: false },
    { start: 2.7, text: "On it, checking now", mine: true },
    { start: 5.2, text: "Resolved", mine: false },
  ];
  return (
    <motion.div
      {...cardMotion(0.95, 5)}
      className="absolute bottom-[4%] left-[0%] z-20 w-[175px] rounded-xl bg-white border border-slate-200 shadow-lg p-3 rotate-2"
    >
      <p className="text-[10px] font-semibold text-[#003333] mb-2">Team chat</p>
      <div className="h-[86px] overflow-hidden flex flex-col justify-end gap-1.5">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
            <motion.div
              className={`text-[9px] px-2 py-1 rounded-lg max-w-[80%] ${
                m.mine ? "bg-[#003333] text-white" : "bg-slate-100 text-slate-600"
              }`}
              initial={{ opacity: 0, scale: 0.6, y: 6 }}
              animate={{
                opacity: [0, 0, 1, 1, 0],
                scale: [0.6, 0.6, 1, 1, 0.9],
                y: [6, 6, 0, 0, 0],
              }}
              transition={{
                duration: cycle,
                repeat: Infinity,
                ease: "easeOut",
                times: [0, m.start / cycle, (m.start + 0.5) / cycle, 0.92, 1],
              }}
            >
              {m.text}
            </motion.div>
          </div>
        ))}
        <motion.div
          className="flex justify-start gap-0.5 pl-1"
          animate={{ opacity: [0, 1, 0, 1, 0, 1, 0, 0] }}
          transition={{
            duration: cycle,
            repeat: Infinity,
            times: [0, 0.08, 0.14, 0.28, 0.34, 0.6, 0.66, 1],
          }}
        >
          {[0, 1, 2].map((d) => (
            <motion.span
              key={d}
              className="w-1 h-1 rounded-full bg-slate-400"
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: d * 0.15 }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};

/* ---------- Scheduling: bookings light up in sequence ---------- */
const SchedulingCard = () => (
  <motion.div
    {...cardMotion(1.15, 6.5)}
    className="absolute bottom-[0%] right-[8%] z-10 w-[140px] rounded-xl bg-white border border-slate-200 shadow-lg p-3 -rotate-3"
  >
    <p className="text-[10px] font-semibold text-[#003333] mb-2">Scheduling</p>
    <div className="grid grid-cols-7 gap-1">
      {Array.from({ length: 21 }).map((_, i) => (
        <motion.span
          key={i}
          className="w-2.5 h-2.5 rounded-sm"
          animate={{
            backgroundColor:
              i === 4 || i === 11 || i === 17
                ? ["rgba(0,51,51,0.08)", "#66B3B3", "rgba(0,51,51,0.08)"]
                : "rgba(0,51,51,0.08)",
          }}
          transition={
            i === 4 || i === 11 || i === 17
              ? { duration: 6, repeat: Infinity, delay: i === 4 ? 0 : i === 11 ? 2 : 4, ease: "easeInOut" }
              : undefined
          }
        />
      ))}
    </div>
  </motion.div>
);

/* ---------- Real-time sync ---------- */
const SyncBadge = () => (
  <motion.div
    {...cardMotion(1.35, 5)}
    className="absolute top-[80%] right-[-2%] z-40 rounded-full bg-[#003333] text-white pl-2 pr-3 py-1.5 shadow-lg flex items-center gap-1.5"
  >
    <motion.svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <motion.path
        d="M4 12a8 8 0 0 1 14-5.3M4 12a8 8 0 0 0 14 5.3"
        stroke="#66B3B3"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
        animate={{ rotate: 360 }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: "12px 12px" }}
      />
    </motion.svg>
    <span className="text-[10px] font-medium whitespace-nowrap">Real-time sync</span>
  </motion.div>
);

/* ---------- Secure access: padlock actually locks ---------- */
const SecureBadge = () => (
  <motion.div
    {...cardMotion(1.55, 5.8)}
    className="absolute top-[42%] left-[-4%] z-40 rounded-full bg-white border border-slate-200 text-[#003333] pl-2 pr-3 py-1.5 shadow-lg flex items-center gap-1.5"
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="11" width="14" height="9" rx="2" fill="#66B3B3" opacity="0.15" />
      <rect x="5" y="11" width="14" height="9" rx="2" stroke="#66B3B3" strokeWidth="1.5" />
      <motion.path
        d="M8 11V8a4 4 0 0 1 8 0v3"
        stroke="#003333"
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
        animate={{ rotate: [0, -8, 0], y: [0, -1, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "12px 11px" }}
      />
      <motion.circle
        cx="12"
        cy="15"
        r="1.4"
        fill="#003333"
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
    </svg>
    <span className="text-[10px] font-medium whitespace-nowrap">Secure access</span>
  </motion.div>
);

/* ---------- Joint animation: connectors pulse together from the client hub ---------- */
const ConnectorLines = () => {
  const paths = [
    "M221 189 L110 44",
    "M221 189 L355 55",
    "M221 189 L385 245",
    "M221 189 L90 350",
    "M221 189 L360 380",
  ];
  return (
    <svg viewBox="0 0 460 460" className="absolute inset-0 w-full h-full z-0 pointer-events-none">
      {paths.map((d, i) => (
        <motion.path
          key={i}
          d={d}
          fill="none"
          stroke="#003333"
          strokeWidth="1"
          strokeOpacity="0.18"
          strokeDasharray="4 5"
          animate={{ strokeDashoffset: [0, -18], strokeOpacity: [0.14, 0.32, 0.14] }}
          transition={{
            strokeDashoffset: { duration: 2.5, repeat: Infinity, ease: "linear" },
            strokeOpacity: { duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 },
          }}
        />
      ))}
    </svg>
  );
};

export const HeroIllustration = () => {
  return (
    <div className="relative aspect-square max-w-lg mx-auto">
      <div className="absolute top-10 right-10 w-40 h-40 bg-[#66B3B3]/10 rounded-full blur-2xl" />
      <div className="absolute bottom-10 left-10 w-32 h-32 bg-[#003333]/10 rounded-full blur-2xl" />
      <ConnectorLines />
      <KanbanCard />
      <WebsiteCard />
      <ClientHubCard />
      <ThroughputCard />
      <ChatCard />
      <SchedulingCard />
      <SyncBadge />
      <SecureBadge />
    </div>
  );
};
